import { useContext, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  Tooltip,
  WelcomeElement,
} from '@components';
import { GlobalContext, UserPreferencesContext } from '@contexts';
import { ImageviewIcon, ListviewIcon, SearchIcon } from '@svgs';
import { businessQuery, locationsQuery, sitesQuery } from '@queries';

import { ProjectBreadcrumbs } from '../components/ProjectBreadcrumbs/ProjectBreadcrumbs.jsx';
import ProjectSidebar from '../components/ProjectSidebar/ProjectSidebar.jsx';

import WebsiteCard from './WebsiteCard/WebsiteCard.jsx';
import WebsiteRow from './WebsiteRow/WebsiteRow.jsx';

import './Websites.scss';

const VIEW_TYPES = {
  CARDS: 'cards',
  LIST: 'list',
};

const matchWebsite = (site, query, locations) => {
  //get the associated location for the site
  const location = site.location_ids[0] ? locations[site.location_ids[0]] : {};
  // determine filter match for a particular Site
  // explicit matching only
  const exactMatchFields = [site.id.toString(), site.hash];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // non explicit match
  const locationName = location?.location_name?.split(' ') || '';
  // string match for fields
  const siteFields = [
    site.formatted_url,
    site.partner_site_id,
    site.title,
    location?.city || '',
    ...locationName,
    location?.state || '',
    location?.street || '',
  ];

  const queryWords = query.split(' ');

  return siteFields.some((v) =>
    queryWords.some((word) => v && v.toLowerCase().indexOf(word) > -1),
  );
};

const filterWebsites = (sites, query, locations) => {
  // client side filtering of websites
  const cleanedQuery = query.toLowerCase().trim();
  return sites.filter((site) => {
    if (matchWebsite(site, cleanedQuery, locations)) {
      return site;
    }
  });
};

const Websites = () => {
  const { t, i18n } = useTranslation();
  const { businessID } = useParams();
  const globals = useContext(GlobalContext);
  const [userPreferences, setUserPreferences] = useContext(
    UserPreferencesContext,
  );

  // filter search term
  const [searchTerm, setSearchTerm] = useState('');

  // list vs cards view state
  const [viewType, setViewType] = useState(null);

  // Fetches business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // Fetch sites
  const {
    data: websites,
    isPending: websitesIsPending,
    isError,
  } = useQuery(
    sitesQuery({
      business_id: businessID,
      deleted: 0,
      limit: 1000,
      order_by: 'base_directory',
      type: 'default',
      _fields: [
        'id',
        'adjusted_base_directory',
        'display_url',
        'screenshot_thumbnail',
        'location_ids',
        'published',
        'formatted_url',
        'preview_url',
        'base_directory',
        'business_id',
        'title',
        'has_draft',
      ].join(','),
    }),
  );

  // Fetch locations
  const { data: locationsData, isPending: locationsIsPending } = useQuery(
    locationsQuery({
      business_id: businessID,
      limit: 1000,
      _fields: [
        'id',
        'city',
        'service_area_only',
        'street',
        'street2',
        'state',
        'postal_code',
        'location_name',
      ].join(','),
    }),
  );
  // make lookup of Locations by ID for reference in location details
  const locationsByID = locationsData
    ? locationsData.objects.reduce(
        (mapping, currentValue) => ({
          ...mapping,
          [currentValue.id]: currentValue,
        }),
        {},
      )
    : null;

  // determine which view type to use
  useEffect(() => {
    if (!globals.visible.websitesFiltering) {
      setViewType(VIEW_TYPES.CARDS);
    } else {
      if (userPreferences[`websiteViewType-${businessID}`]) {
        // set view type from user preferences
        setViewType(userPreferences[`websiteViewType-${businessID}`]);
        return;
      }
      if (!websites) {
        // no websites yet
        return;
      }
      // using the websites response length to determine type
      if (websites.objects.length <= 20) {
        setViewType(VIEW_TYPES.CARDS);
      } else if (websites.objects.length >= 21) {
        setViewType(VIEW_TYPES.LIST);
      }
    }
  }, [websites, userPreferences[`websiteViewType-${businessID}`]]);

  const setViewTypePreference = (vt) => {
    // set the view type and set the user preference for future reloads
    setUserPreferences({
      [`websiteViewType-${businessID}`]: vt,
    });
  };

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // https://medium.com/nerd-for-tech/debounce-your-search-react-input-optimization-fd270a8042b
  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);
  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // filter websites if there is a search term, otherwise return the full list
  const filteredWebsites =
    searchTerm && locationsByID && websites?.objects
      ? filterWebsites(websites.objects, searchTerm, locationsByID)
      : websites?.objects || [];

  return (
    <>
      <Header />
      <BodyWrapper>
        <ProjectSidebar businessID={businessID} businessData={businessData} />
        <MainWrapper>
          <ProjectBreadcrumbs businessData={businessData}>
            <li className="sb-flex">
              <span>{t('Websites')}</span>
            </li>
          </ProjectBreadcrumbs>
          <div className="sb-websites">
            <div className="sb-manage-content-head">
              <h1>{t('Websites')}</h1>
              {globals.perms.Site.includes('add') && (
                <div className="sb-button-list-inline">
                  <Link
                    to={`/dashboard/projects/${businessID}/sites/new`}
                    className="sb-btn sb-btn-cancel"
                  >
                    Add new
                  </Link>
                </div>
              )}
            </div>
            {globals.visible.websitesFiltering && (
              <div className="sb-manage-options-menu sb-flex">
                <div className="sb-manage-options-left"></div>
                <div className="sb-manage-options-right">
                  <div className="sb-flex">
                    <form
                      role="search"
                      className="sb-manage-options-search"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <input
                        type="search"
                        name="query"
                        placeholder="Filter"
                        aria-label="Enter search term"
                        autoComplete="off"
                        defaultValue={searchTerm}
                        onChange={debouncedResults}
                        disabled={websitesIsPending}
                      />
                      <SearchIcon />
                    </form>
                    <Tooltip tooltip="View as list">
                      <button
                        className={
                          `sb-manage-view-list sb-manage-view-btn ` +
                          (viewType == VIEW_TYPES.LIST
                            ? 'sb-manage-view-btn-active'
                            : '')
                        }
                        onClick={() => setViewTypePreference(VIEW_TYPES.LIST)}
                      >
                        <ListviewIcon />
                      </button>
                    </Tooltip>
                    <Tooltip tooltip="View as tiles">
                      <button
                        className={
                          `sb-manage-view-image sb-manage-view-btn ` +
                          (viewType == VIEW_TYPES.CARDS
                            ? 'sb-manage-view-btn-active'
                            : '')
                        }
                        onClick={() => setViewTypePreference(VIEW_TYPES.CARDS)}
                      >
                        <ImageviewIcon />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
            {websitesIsPending && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {!websitesIsPending &&
              (isError || websites.objects?.length == 0) && (
                <WelcomeElement>
                  <p>No active websites under this project</p>
                  <Link
                    to={`/dashboard/projects/${businessID}/sites/new`}
                    className="sb-btn sb-btn-save"
                  >
                    Add first website
                  </Link>
                </WelcomeElement>
              )}
            {!websitesIsPending && !isError && viewType == VIEW_TYPES.CARDS && (
              <div className="sb-grid sb-websites-wrap">
                {filteredWebsites.map((website) => {
                  return (
                    <WebsiteCard
                      key={website.id}
                      website={website}
                      locationsByID={locationsByID}
                    />
                  );
                })}
                {/* below elements needed to fill the grid */}
                <div className="sb-website-item"></div>
                <div className="sb-website-item"></div>
                <div className="sb-website-item"></div>
              </div>
            )}
            {!websitesIsPending && !isError && viewType == VIEW_TYPES.LIST && (
              <div className="sb-content-view-row">
                {filteredWebsites.map((website) => {
                  return (
                    <WebsiteRow
                      key={website.id}
                      website={website}
                      locationsByID={locationsByID}
                    />
                  );
                })}
              </div>
            )}
            {!websitesIsPending && searchTerm && (
              <Message
                messageStyle="notice"
                title={
                  `Filtered to ${filteredWebsites.length} of ${websites.objects.length} website` +
                  (websites.objects.length != 1 ? 's' : '')
                }
                fadeoutMessage={false}
                isFormMessage={false}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};

export default Websites;
